// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  PRODUCT_FIELD_TYPE_DATA,
  ProductFieldType,
} from '@/constants/product-field-type';
import { ItemDetail, ItemField } from '@/items/api/types';
import { useCallback, useMemo, useState } from 'react';

export interface ValidationError {
  type:
    | 'field_mismatch'
    | 'hubspot_conflict'
    | 'inventory_mismatch'
    | 'crm_conflict';
  fieldName?: string;
  primaryFieldType?: string;
  conflictingFieldType?: string;
  primaryItemName?: string;
  selectedItemName?: string;
  message?: string;
}

interface ValidationResult {
  isValid: boolean;
  error: ValidationError | null;
  validate: (primaryItem: ItemDetail, selectedItem: ItemDetail) => boolean;
  clearError: () => void;
}

function getFieldTypeLabel(type: ProductFieldType): string {
  return PRODUCT_FIELD_TYPE_DATA[type].fieldTypeLabel;
}

export function useMergeValidation(): ValidationResult {
  const [error, setError] = useState<ValidationError | null>(null);

  const validate = useCallback(
    (primaryItem: ItemDetail, selectedItem: ItemDetail): boolean => {
      // Check for field type conflicts - optimized O(n) algorithm
      const primaryFields = primaryItem.fields;
      const selectedFields = selectedItem.fields;

      // Create a Map for O(1) lookup of selected fields by lowercase label
      const selectedFieldMap = new Map<string, ItemField>();
      for (const selectedField of selectedFields) {
        selectedFieldMap.set(selectedField.label.toLowerCase(), selectedField);
      }

      // Check for conflicts in O(n) time
      for (const primaryField of primaryFields) {
        const conflictingField = selectedFieldMap.get(
          primaryField.label.toLowerCase()
        );

        if (conflictingField && conflictingField.type !== primaryField.type) {
          setError({
            type: 'field_mismatch',
            fieldName: primaryField.label,
            primaryFieldType: getFieldTypeLabel(primaryField.type),
            conflictingFieldType: getFieldTypeLabel(conflictingField.type),
            primaryItemName: primaryItem.name,
            selectedItemName: selectedItem.name,
          });
          return false;
        }
      }

      // Check inventory type validation
      // Only block non-physical (SERVICE) trying to merge with physical (DEVICE/BULK)
      // Physical items can merge with non-physical items
      if (!primaryItem.isPhysical && selectedItem.isPhysical) {
        setError({
          type: 'inventory_mismatch',
          message:
            'Non-physical items (SERVICE) can only be merged with other non-physical items.',
        });
        return false;
      }

      // Check integration validation rules (NetSuite items are allowed to merge)
      // Get linked integration types for each item, excluding NetSuite
      const primaryLinkedTypes = primaryItem.integrations
        .filter((link) => link.link !== null && link.id !== 'NETSUITE')
        .map((link) => link.id);
      const selectedLinkedTypes = selectedItem.integrations
        .filter((link) => link.link !== null && link.id !== 'NETSUITE')
        .map((link) => link.id);

      // Check if both items are linked to the same integration type
      for (const primaryType of primaryLinkedTypes) {
        if (selectedLinkedTypes.includes(primaryType)) {
          setError({
            type: 'crm_conflict',
            message:
              'Both items are linked to CRM systems. Please merge them in your CRM first and then merge in Hardfin.',
          });
          return false;
        }
      }

      // All validations passed
      setError(null);
      return true;
    },
    []
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Memoize the result object to prevent unnecessary re-renders
  const result = useMemo(
    () => ({
      isValid: error === null,
      error,
      validate,
      clearError,
    }),
    [error, validate, clearError]
  );

  return result;
}
